import {
  getCancelReasonLabel,
  CANCEL_REASON_LABELS,
} from './order-history.helper';

/**
 * One status transition recorded by the backend on stock and OTC orders.
 * The array is chronological (oldest first) and entry n's `to` equals entry n+1's `from`,
 * so it reads as a chain. `from` is null on the first entry (order creation).
 */
export interface StatusHistoryEntry {
  date: string;
  from: string | null;
  to: string;
  /** Null whenever `source` is `system` or `provider` — nobody human was involved. */
  userId: string | null;
  source: string;
  reason: string | null;
  /** Free text behind `reason` — today only the broker's rejection text. Usually null. */
  message?: string | null;
}

/**
 * A resolved `StatusHistoryEntry.userId` — a client or a staff member. Same fields as the
 * row `userInfo` the order lists carry, plus `role` to tell the two apart.
 */
export interface StatusHistoryActor {
  userId: string;
  role: string | null;
  email: string;
  brokerageAccount: string;
  firstName: string;
  lastName: string;
  companyName: string;
  phone: string;
  dateOfBirth: string | null;
}

export const STATUS_SOURCE_LABELS: Record<string, string> = {
  user: 'Client',
  admin: 'Admin',
  system: 'System',
  provider: 'Broker (GTN)',
};

export function getStatusSourceLabel(source: string): string {
  return STATUS_SOURCE_LABELS[source] ?? source;
}

export function getStatusSourceColor(source: string): string {
  switch (source) {
    case 'user':
      return 'blue';
    case 'admin':
      return 'purple';
    case 'provider':
      return 'geekblue';
    case 'system':
      return 'default';
    default:
      return 'default';
  }
}

/** Reasons the backend sets on non-cancel transitions. */
export const LIFECYCLE_REASON_LABELS: Record<string, string> = {
  CREATE: 'Created',
  MATCH: 'Matched',
  GTN_FILL: 'Filled at Broker',
  STOP_TRIGGERED: 'Stop Triggered',
  ADMIN_ORDER_EXPIRED: 'Admin Order Expired',
  FX_CONVERSION: 'FX Conversion',
  RETURNED_TO_DEALER: 'Returned to Dealer',
};

/**
 * Cancel reasons first, then lifecycle reasons. Anything unrecognised passes through
 * verbatim — an admin force-close writes the operator's free text into `reason`.
 */
export function getStatusHistoryReasonLabel(reason: string | null): string {
  if (!reason) return '';
  if (CANCEL_REASON_LABELS[reason]) return getCancelReasonLabel(reason);
  return LIFECYCLE_REASON_LABELS[reason] ?? reason;
}

export function getStatusHistory(order: any): StatusHistoryEntry[] {
  const history = order?.statusHistory;
  return Array.isArray(history) ? history : [];
}

/** Orders created before the backend deploy return an empty array — that is not an error. */
export function hasStatusHistory(order: any): boolean {
  return getStatusHistory(order).length > 0;
}
